import { usePermissions } from "@/hooks/auth/usePermissions";
import {
  useEntityData,
  useEntityLabelDropdown,
  useFrequentEntities,
  useMyEntities,
} from "@/hooks/entities/useEntitiesQueries";
import { useRecordEntityVisit } from "@/hooks/entities/useEntitiesMutations";
import {
  PLATFORM_TENANT_ID,
  SERVENGINE_SCOPE,
  useEntityTenantStore,
} from "@/store/entityTenantStore";
import { useTableFilterBaseStore } from "@/store/tableFilterStore";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import {
  Divider,
  Group,
  Image,
  Menu,
  Paper,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { IconBuildings, IconChevronDown, IconSearch } from "@tabler/icons-react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "@onengine/component-library";
import { truncateValue } from "@/utils/truncateValue";
import axiosInstance from "@/lib/axiosInstance";
import { routerRef } from "@/lib/routerRef";
import { applySessionResponseToStore } from "@/utils/authHelpers";
import { clearListSearchForPick } from "@/utils/listSearchOnPick";
import { showErrorAlert } from "@/utils/responseHandler";
import { useS3ImageUrl } from "@/hooks/s3/useS3ImageUrl";
import { useSubdomainBranding } from "@/hooks/branding/useSubdomainBranding";
import type { EntityDropdownOption, MyEntity } from "@/api/entities/entities.queries.types";
import styles from "./LabelMenu.module.scss";

type LabelMenuProps = {
  opened: boolean;
  isClientPage?: boolean;
};

const LABEL_MAX_LENGTH = 20;

/**
 * The unscoped platform view. Selecting it clears the acting entity, so lists go
 * back to spanning every entity — which is what a super admin sees today before
 * they pick anything, and what "ServEngine" is here to name.
 */
const SERVENGINE_OPTION: EntityDropdownOption = {
  label: "ServEngine",
  value: SERVENGINE_SCOPE,
  logoUrl: null,
};

/** Roughly five rows before the list starts scrolling. */
const MENU_MAX_HEIGHT = 288;

// Local-only system switcher value: lets a ServEngine super-admin hop between the
// platform-admin view and any entity from the top-left label. Production entity
// context comes from subdomains, so this replaces that path on localhost only.
const ADMIN_VALUE = "__servengine_admin__";
const ADMIN_OPTION: EntityDropdownOption = { ...SERVENGINE_OPTION, value: ADMIN_VALUE };

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_PLACEHOLDER = "Search entities";
const SEARCHING_TEXT = "Searching...";
const NO_MATCH_TEXT = "No entities match";

const toMyEntityOption = (entity: MyEntity): EntityDropdownOption => ({
  label: entity.entityName ?? "",
  value: entity.id,
  logoUrl: null,
});
const FREQUENT_GROUP_LABEL = "Frequently visited";
const ALL_ENTITIES_GROUP_LABEL = "All entities";

function TruncatedLabel({ label }: { label: string }) {
  const isTruncated = label.length > LABEL_MAX_LENGTH;
  const text = (
    <Text
      size="sm"
      fw={"var(--font-weight-semi-bold)"}
      truncate
      style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap" }}
    >
      {isTruncated ? truncateValue(label, LABEL_MAX_LENGTH) : label}
    </Text>
  );

  return isTruncated ? (
    <Tooltip content={label} pointer="top-center">
      {text}
    </Tooltip>
  ) : (
    text
  );
}

export function LabelChip({ logoUrl }: { logoUrl?: string | null }) {
  if (logoUrl) {
    return <Image src={logoUrl} alt="Entity logo" w={28} h={28} radius={8} fit="cover" />;
  }

  return (
    <Group
      w={28}
      h={28}
      align="center"
      justify="center"
      style={{
        borderRadius: 8,
        background: "var(--bg-brand-default)",
      }}
    >
      <IconBuildings size={16} color="white" />
    </Group>
  );
}

function EntityOptionButton({
  option,
  active,
  onClick,
}: {
  option: EntityDropdownOption;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      data-testid={`entity-switcher-option-${option.value}`}
      onClick={onClick}
      aria-selected={active}
      className={clsx(styles.option, active && styles.optionActive)}
    >
      <Group gap="sm" px="md" py="sm" style={{ minWidth: 0 }}>
        <LabelChip logoUrl={option.logoUrl} />
        <Text fw={"var(--font-weight-semi-bold)"} truncate style={{ flex: 1, minWidth: 0 }}>
          {option.label}
        </Text>
      </Group>
    </UnstyledButton>
  );
}

type EntitySearchListProps = {
  search: string;
  onSearchChange: (value: string) => void;
  pinnedOption: EntityDropdownOption;
  suggestions: EntityDropdownOption[];
  options: EntityDropdownOption[];
  total: number | undefined;
  isFetching: boolean;
  selectedValue: string;
  onSelect: (value: string) => void;
};

/**
 * The picker searches server-side instead of listing every entity: the dropdown
 * endpoint pages, so listing could only ever show the first page and hid the rest
 * (SERV-2729). The pinned option (ServEngine) always leads as the way back out.
 */
function EntitySearchList({
  search,
  onSearchChange,
  pinnedOption,
  suggestions,
  options,
  total,
  isFetching,
  selectedValue,
  onSelect,
}: EntitySearchListProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // The localhost switcher renders this in a plain Paper with no focus trap, so
  // focus the input directly there. Inside Menu the focus trap owns it via
  // data-autofocus, which only wins once Menu's own placeholder is turned off.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const hasQuery = search.trim().length > 0;
  // Suggestions belong to the empty state only: once a term is typed the user
  // is looking for something specific and the ranked list would be noise
  // (SERV-2793). Entities already offered as suggestions are not repeated in
  // the first page below.
  const visibleSuggestions = hasQuery ? [] : suggestions;
  const suggestedValues = new Set(visibleSuggestions.map((option) => option.value));
  const remainingOptions = options.filter((option) => !suggestedValues.has(option.value));
  // Previous results stay on screen while the next page loads, so the indicator
  // cannot depend on the list being empty or a stale page would read as the
  // answer to the new term.
  const showSearching = isFetching;
  const showNoMatch = !isFetching && hasQuery && options.length === 0;
  const showNarrowHint = !isFetching && total !== undefined && total > options.length;

  return (
    <>
      <TextInput
        ref={inputRef}
        className={styles.searchInput}
        size="sm"
        placeholder={SEARCH_PLACEHOLDER}
        aria-label={SEARCH_PLACEHOLDER}
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(event) => onSearchChange(event.currentTarget.value)}
        data-autofocus
      />
      {/*
        ScrollArea.Autosize, not a maxHeight on the surrounding Paper: the cap
        has to land on Mantine's scroll viewport. A max-height on an outer
        element leaves the viewport's height:100% resolving against a
        max-height-only parent, so it grows to content, Mantine decides there
        is no overflow and hides its scrollbars, and the list is clipped with
        no way to reach the rest. Matches TimePopover and BulkImportModal
        (SERV-2494 row 1.2).
      */}
      <ScrollArea.Autosize mah={MENU_MAX_HEIGHT} scrollbars="y">
        <EntityOptionButton
          option={pinnedOption}
          active={pinnedOption.value === selectedValue}
          onClick={() => onSelect(pinnedOption.value)}
        />
        {visibleSuggestions.length > 0 ? (
          <div role="group" aria-label={FREQUENT_GROUP_LABEL}>
            <Text
              size="xs"
              c="dimmed"
              fw={"var(--font-weight-semi-bold)"}
              className={styles.groupLabel}
            >
              {FREQUENT_GROUP_LABEL}
            </Text>
            {visibleSuggestions.map((option) => (
              <EntityOptionButton
                key={`frequent-${option.value}`}
                option={option}
                active={option.value === selectedValue}
                onClick={() => onSelect(option.value)}
              />
            ))}
            {/*
              The two lists share one look, so without a rule and a second
              heading the suggestions read as the top of the alphabet.
            */}
            <Divider className={styles.groupDivider} />
            <Text
              size="xs"
              c="dimmed"
              fw={"var(--font-weight-semi-bold)"}
              className={styles.groupLabel}
            >
              {ALL_ENTITIES_GROUP_LABEL}
            </Text>
          </div>
        ) : null}
        {remainingOptions.map((option) => (
          <EntityOptionButton
            key={option.value}
            option={option}
            active={option.value === selectedValue}
            onClick={() => onSelect(option.value)}
          />
        ))}
        {showSearching ? (
          <Text size="sm" c="dimmed" className={styles.stateText}>
            {SEARCHING_TEXT}
          </Text>
        ) : null}
        {showNoMatch ? (
          <Text size="sm" c="dimmed" className={styles.stateText}>
            {NO_MATCH_TEXT}
          </Text>
        ) : null}
        {showNarrowHint ? (
          <Text size="sm" c="dimmed" className={styles.stateText}>
            {`${total} entities match. Keep typing to narrow the list.`}
          </Text>
        ) : null}
      </ScrollArea.Autosize>
    </>
  );
}

const LabelMenu = ({ opened, isClientPage = false }: LabelMenuProps) => {
  const { setEntityTenant, entityTenant } = useEntityTenantStore();
  const { isEntityLogin, entityName, tenantId, isAppLevel } = useActiveSession();
  const { isSuperAdmin } = usePermissions();
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  const isLocalEnv = import.meta.env.VITE_APP_ENV === "localhost";
  // SERV-3232: an app-level user switches between the entities she belongs to,
  // listed by /my-entities; the super-admin endpoints below refuse her. Not while
  // impersonated: the interceptor never sends the acting-entity header alongside
  // the impersonation header, so a pick there would change the chip and nothing else.
  const isAppLevelSwitcher =
    !isClientPage && (isAppLevel ?? false) && !isSuperAdmin && !isEntityLogin && !isImpersonating;
  const canSwitch =
    !isClientPage && ((isSuperAdmin && (!isEntityLogin || isLocalEnv)) || isAppLevelSwitcher);

  const queryClient = useQueryClient();
  const [isMenuOpened, menuHandlers] = useDisclosure();
  const [isSwitching, setIsSwitching] = useState(false);
  const pendingPickRef = useRef<{ scope: string; at: number } | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  // The chip label no longer comes from this list, so nothing needs the page
  // before the menu is open; fetching it on every page load would be waste.
  const { data: entityLabelDropdown, isFetching: isFetchingLabelDropdown } = useEntityLabelDropdown(
    canSwitch && isMenuOpened && !isAppLevelSwitcher,
    debouncedSearch.trim(),
  );
  const { data: myEntities, isFetching: isFetchingMyEntities } = useMyEntities(isAppLevelSwitcher);
  const myEntityOptions: EntityDropdownOption[] = (myEntities ?? []).map(toMyEntityOption);
  const trimmedSearch = debouncedSearch.trim().toLowerCase();
  const visibleMyEntityOptions = trimmedSearch
    ? myEntityOptions.filter((option) => option.label.toLowerCase().includes(trimmedSearch))
    : myEntityOptions;
  const isFetching = isAppLevelSwitcher ? isFetchingMyEntities : isFetchingLabelDropdown;

  const labelOptions: EntityDropdownOption[] = isAppLevelSwitcher
    ? visibleMyEntityOptions
    : (entityLabelDropdown?.data ?? []);

  // Same gate as the search page: only a super admin with the menu open has
  // visits to read, and the endpoint answers 403 to anyone else.
  const { data: frequentEntities } = useFrequentEntities(
    canSwitch && isMenuOpened && !isAppLevelSwitcher,
  );
  const suggestions = (frequentEntities ?? []).filter(
    (option) => option.value !== PLATFORM_TENANT_ID,
  );
  const recordVisit = useRecordEntityVisit();

  // The platform tenant owns no work orders, clients or invoices, so offering it
  // as a destination would scope every list to nothing. "ServEngine" expresses
  // that state properly and maps to SERVENGINE_SCOPE.
  const entityOptions = labelOptions.filter((option) => option.value !== PLATFORM_TENANT_ID);

  // The server total counts the platform row this list hides; without the
  // correction every deployment with fewer than a page of entities would be
  // told more match than are shown.
  const hiddenPlatformRows = labelOptions.length - entityOptions.length;
  const totalEntities = isAppLevelSwitcher
    ? entityOptions.length
    : entityLabelDropdown === undefined
      ? undefined
      : entityLabelDropdown.total - hiddenPlatformRows;

  // `entityTenant` is the ONE source of truth for the selection — it is what the
  // axios interceptor sends, so any second copy of it in component state can
  // disagree with what the server is actually scoping to.
  const selectedValue = isEntityLogin ? (tenantId ?? SERVENGINE_SCOPE) : entityTenant;

  // Declared here rather than beside switchContext, where dev has it, because
  // the scope effects below read it.
  const isLocalAdminSwitcher = isLocalEnv && isSuperAdmin && !isClientPage;

  useEffect(() => {
    if (isEntityLogin && tenantId) {
      setEntityTenant(tenantId);
    }
  }, [isEntityLogin, tenantId, setEntityTenant]);

  const closeMenu = () => {
    menuHandlers.close();
    setSearch("");
  };

  // Self-heal a scope left behind by an earlier session switch.
  //
  // On the local switcher the server session is the source of truth: scope
  // changes go through switchContext, which swaps the session and reloads. So
  // "the session is not an entity login" and "entityTenant names an entity" is
  // a contradiction, and it is one that persists, because this store is in
  // localStorage and a reload does not clear it. While it lasts, axiosInstance
  // keeps sending acting-entity-id for an entity the session already left, and
  // every entity-scoped list silently narrows to it.
  //
  // The effect above cannot catch this: it only fires when the stored entity is
  // MISSING from the dropdown, and an entity you merely left is still a valid
  // option. This one keys off the session instead of the option list.
  //
  // Scoped to the local switcher deliberately. On a client page, and in
  // production, a platform user picking an entity through selectEntity is a
  // legitimate non-entity-login session with a scope set, and clearing it there
  // would undo the selection they just made.
  //
  // Invalidates for the same reason selectEntity does, and it matters more
  // here: this fires on a page that has ALREADY rendered. By the time this
  // effect can fire, the loader and the first-render queries have gone out
  // carrying the stale acting-entity header. The scoped list keys move to the
  // new scope on their own; the keys that do not mention the entity still hold
  // the left entity's rows, and staleTime keeps them there until the user
  // navigates away and back unless they are refetched now.
  useEffect(() => {
    if (!isLocalAdminSwitcher) return;
    // A switch in flight has already stored the scope it is moving to, and the
    // session it is moving to has not necessarily landed in the store yet.
    // Without this guard the heal reads that pairing as the contradiction it is
    // looking for and wipes the new scope in the same tick switchContext set
    // it. The reload usually wins that race, which is the worst way for it to
    // be wrong: correct in practice, and silently not in principle.
    if (isSwitching) return;
    if (isEntityLogin) return;
    if (entityTenant === SERVENGINE_SCOPE) return;
    setEntityTenant(SERVENGINE_SCOPE);
    void queryClient.invalidateQueries();
  }, [
    isLocalAdminSwitcher,
    isSwitching,
    isEntityLogin,
    entityTenant,
    setEntityTenant,
    queryClient,
  ]);

  /**
   * SERV-3232: switching scope invalidates rather than clears. The merged lists,
   * their option queries and permissions carry the scope in their key and send it
   * with the call, so they move to the new scope and keep the old one's entries
   * for the way back; every other query still keys without the entity, so it is
   * refetched under the new header instead of waiting out its staleTime.
   * Nothing is dropped, so nothing that already has data suspends.
   *
   * On a list, the scope moves only once the list's filters are out of the URL,
   * because the list remounts on the new scope and reads them back from there.
   * In-flight requests went out under the old scope, so they are cancelled and
   * refetched with the rest.
   */
  const selectEntity = (value: string) => {
    if (value === entityTenant) return;
    useTableFilterBaseStore.getState().resetAllFilters();
    const switchScope = () => {
      void queryClient.cancelQueries();
      pendingPickRef.current = { scope: value, at: Date.now() };
      setEntityTenant(value);
      // After the scope change, never before it: the switch must not wait on the
      // network, and ServEngine is the unscoped view, not an entity anyone
      // visited (SERV-2793).
      if (value !== SERVENGINE_SCOPE && !isAppLevelSwitcher) {
        recordVisit.mutate(value);
      }
    };
    const clearingListSearch = clearListSearchForPick(routerRef.current);
    if (clearingListSearch) void clearingListSearch.then(switchScope, switchScope);
    else switchScope();
  };

  // SERV-3232: not from selectEntity. Until the pick has rendered, and a list's
  // own effect has moved its query onto the new key (later in this same flush),
  // the old keys are still mounted and an invalidate refetches them for a scope
  // nothing reads any more. Queries fetched since the pick already answer for the
  // new scope.
  useEffect(() => {
    const pendingPick = pendingPickRef.current;
    if (!pendingPick || pendingPick.scope !== entityTenant) return;
    pendingPickRef.current = null;
    setTimeout(() => {
      void queryClient.invalidateQueries(
        { predicate: (query) => query.state.dataUpdatedAt < pendingPick.at },
        { cancelRefetch: false },
      );
    }, 0);
  }, [entityTenant, queryClient]);

  // ADMIN_VALUE and isLocalAdminSwitcher both live above now: the first at
  // module scope from SERV-2729, the second beside the effects that read it.

  // No role grants the `entity` permission, so GET /entity/:id 403s for everyone
  // except a super admin, who the backend waves through on user type alone
  // (authorizationMiddleware). Asking anyway is what put an "Access denied" toast
  // on every navigation in SERV-2751, so mirror that one rule here. This is
  // checked ahead of every other branch, including isClientPage: the Add New
  // Client wizard renders this same chip and entity roles can reach it.
  const canReadEntityRecord = isSuperAdmin;

  const activeEntityId = !canReadEntityRecord
    ? null
    : isClientPage
      ? entityTenant || null
      : isEntityLogin
        ? (tenantId ?? null)
        : isLocalAdminSwitcher
          ? null
          : selectedValue || null;

  const { data: activeEntity } = useEntityData(activeEntityId ?? "");
  const entityRecordLogoUrl = useS3ImageUrl(activeEntity?.logoS3Key);
  // Branding is resolved from the subdomain, so it only carries a logo for an
  // entity login; a super admin sits on the main app domain and reads the record.
  const subdomainBranding = useSubdomainBranding();
  const activeLogoUrl = canReadEntityRecord
    ? entityRecordLogoUrl
    : (subdomainBranding?.logoUrl ?? null);

  // The details fetch is the label's source of truth: a search page only holds
  // the entities that match the current term, so the selected one is usually
  // not in it. A selection the server no longer accepts (deleted or
  // deactivated) is reset by the INVALID_ACTING_ENTITY path in axiosInstance,
  // which the acting-entity middleware triggers on the very next request.
  const selectedLabel =
    activeEntity?.entityName ??
    myEntityOptions.find((option) => option.value === selectedValue)?.label ??
    entityOptions.find((option) => option.value === selectedValue)?.label ??
    SERVENGINE_OPTION.label;

  const switchContext = async (value: string) => {
    if (isSwitching) return;
    setIsSwitching(true);
    try {
      const { data } =
        value === ADMIN_VALUE
          ? await axiosInstance.post("/session-management/local-switch-to-admin")
          : await axiosInstance.post("/session-management/local-enter-entity", {
              entityId: value,
            });
      applySessionResponseToStore(data);
      // Keep the acting-entity scope in step with the session we just switched
      // into. axiosInstance sends `entityTenant` as the acting-entity-id header
      // on every request, this store is persisted to localStorage, and the
      // reload below does not clear it — so without this, leaving an entity for
      // ServEngine kept sending the entity we just left. The server then read
      // the request as an entity login while the menu said "ServEngine", and
      // every entity-scoped list narrowed to that one entity. The payment
      // account sharing picker is where it showed worst: its options are the
      // entity list minus the account's owner, so one entity in meant zero out
      // and the dropdown looked empty rather than wrong.
      //
      // The option-list fallback effect earlier in this component could not
      // catch this: it only resets when the stored entity is absent from the
      // dropdown, and an entity you merely left is still perfectly valid. The
      // session-keyed effect beside it does catch it, but only on the next
      // load; this line is what keeps the scope right without waiting for one.
      setEntityTenant(value === ADMIN_VALUE ? SERVENGINE_SCOPE : value);
      window.location.href = "/";
    } catch {
      setIsSwitching(false);
      showErrorAlert("Failed to switch context.", "Switch Failed");
    }
  };

  if (isLocalAdminSwitcher) {
    const currentValue = isEntityLogin ? (tenantId ?? "") : ADMIN_VALUE;
    const currentLabel = isEntityLogin ? (entityName ?? "Entity") : SERVENGINE_OPTION.label;

    return (
      <div style={{ position: "relative", width: "100%" }}>
        <UnstyledButton
          data-testid="entity-switcher-trigger"
          style={{ width: "100%" }}
          disabled={isSwitching}
          onClick={menuHandlers.toggle}
        >
          <Group
            px="xs"
            py={6}
            gap="xs"
            justify={opened ? "space-between" : "center"}
            align="center"
            style={{ minWidth: 0 }}
          >
            <Group
              gap="sm"
              justify={opened ? "flex-start" : "center"}
              style={{ minWidth: 0, flex: 1 }}
            >
              <LabelChip logoUrl={activeLogoUrl} />
              {opened ? <TruncatedLabel label={currentLabel} /> : null}
            </Group>
            {opened ? <IconChevronDown size={16} style={{ flexShrink: 0 }} /> : null}
          </Group>
        </UnstyledButton>

        {isMenuOpened ? (
          <>
            <div onClick={closeMenu} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
            <Paper
              shadow="sm"
              radius="md"
              withBorder
              p="xs"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: 6,
                width: 260,
                zIndex: 201,
                background: "var(--mantine-color-body, white)",
              }}
            >
              <EntitySearchList
                search={search}
                onSearchChange={setSearch}
                pinnedOption={ADMIN_OPTION}
                suggestions={suggestions}
                options={entityOptions}
                total={totalEntities}
                isFetching={isFetching}
                selectedValue={currentValue}
                onSelect={(value) => {
                  closeMenu();
                  if (value !== currentValue) void switchContext(value);
                }}
              />
            </Paper>
          </>
        ) : null}
      </div>
    );
  }

  // If entity login, only show label chip with entity name
  if (isEntityLogin) {
    return (
      <Group
        px="xs"
        py={6}
        gap="xs"
        justify={opened ? "space-between" : "center"}
        align="center"
        style={{ minWidth: 0 }}
      >
        <Group gap="sm" justify={opened ? "flex-start" : "center"} style={{ minWidth: 0, flex: 1 }}>
          <LabelChip logoUrl={activeLogoUrl} />
          {opened ? <TruncatedLabel label={entityName ?? ""} /> : null}
        </Group>
      </Group>
    );
  }

  if (isClientPage) {
    return (
      <Group
        px="xs"
        py={6}
        gap="xs"
        justify={opened ? "space-between" : "center"}
        align="center"
        style={{ minWidth: 0 }}
      >
        <Group gap="sm" justify={opened ? "flex-start" : "center"} style={{ minWidth: 0, flex: 1 }}>
          <LabelChip logoUrl={activeLogoUrl} />
          {opened ? (
            <TruncatedLabel label={activeEntity?.entityName ?? "No entity selected"} />
          ) : null}
        </Group>
      </Group>
    );
  }

  if (!canSwitch) {
    return (
      <Group px="xs" py={6} gap="xs" justify={opened ? "space-between" : "center"} align="center">
        <Group gap="sm" justify={opened ? "flex-start" : "center"}>
          <LabelChip />
          {opened ? (
            <Text size="sm" fw={"var(--font-weight-semi-bold)"} c="dimmed">
              No entities
            </Text>
          ) : null}
        </Group>
      </Group>
    );
  }

  return (
    <Menu
      width={260}
      withArrow
      position="bottom-start"
      offset={6}
      withinPortal
      opened={isMenuOpened}
      onClose={closeMenu}
      closeOnClickOutside={true}
      closeOnEscape={true}
      withInitialFocusPlaceholder={false}
    >
      <Menu.Target>
        <UnstyledButton
          data-testid="entity-switcher-trigger"
          style={{ width: "100%" }}
          onClick={menuHandlers.toggle}
        >
          <Group
            px="xs"
            py={6}
            gap="xs"
            justify={opened ? "space-between" : "center"}
            align="center"
            style={{ minWidth: 0 }}
          >
            <Group
              gap="sm"
              justify={opened ? "flex-start" : "center"}
              style={{ minWidth: 0, flex: 1 }}
            >
              <LabelChip logoUrl={activeLogoUrl} />
              {opened ? <TruncatedLabel label={selectedLabel} /> : null}
            </Group>
            {opened ? <IconChevronDown size={16} style={{ flexShrink: 0 }} /> : null}
          </Group>
        </UnstyledButton>
      </Menu.Target>
      {/*
        Keyed on the scope so a pick unmounts the open dropdown instead of
        animating it out. A pick into a scope with no cached permissions suspends
        this layout (usePermissions suspends on the new scope's key), and when it
        reappears StrictMode replays its effects, which cancels Mantine
        Transition's pending exit and leaves the dropdown on screen with `opened`
        already false, so Escape and outside clicks do nothing.
      */}
      <Menu.Dropdown key={selectedValue} p={0}>
        <Paper shadow="sm" radius="md" withBorder m="xs" p="xs">
          <EntitySearchList
            search={search}
            onSearchChange={setSearch}
            pinnedOption={SERVENGINE_OPTION}
            suggestions={suggestions}
            options={entityOptions}
            total={totalEntities}
            isFetching={isFetching}
            selectedValue={selectedValue}
            onSelect={(value) => {
              selectEntity(value);
              closeMenu();
            }}
          />
        </Paper>
      </Menu.Dropdown>
    </Menu>
  );
};

export default LabelMenu;
